import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, ChevronDown, ChevronRight, Lock } from "lucide-react";

const STORAGE_KEY = "__kd_cookie_consent";
const VERSION = "2026.3";

const CATEGORIES = [
  {
    key: "necessary", locked: true,
    title: "Strictly Necessary & Security",
    desc: "Essential for core platform security, multi-tenant workspace routing, CSRF protection, and authenticating developer sessions. These cannot be disabled.",
    cookies: [
      ["__kd_sess", "security", "encrypted session token", "14 days"],
      ["__kd_csrf", "security", "CSRF mitigation", "session"],
      ["__kd_ws_id", "infrastructure", "tenant routing", "30 days"],
    ],
  },
  {
    key: "functional", locked: false,
    title: "Functional & Interface Preferences",
    desc: "Remembers developer preferences, interface theme, sidebar state, and default regional currency and tax display.",
    cookies: [
      ["__kd_theme", "preferences", "UI theme choice", "1 year"],
      ["__kd_loc", "preferences", "regional tax/currency display default", "180 days"],
    ],
  },
  {
    key: "analytics", locked: false,
    title: "Analytics & Performance Telemetry",
    desc: "Collects aggregated, anonymised metrics on generation request latency, Watchdog execution speed, and navigation friction, to improve platform performance.",
    cookies: [
      ["__kd_telemetry", "analytics", "aggregated performance tracking", "90 days"],
    ],
  },
  {
    key: "marketplace", locked: false,
    title: "Developer Ecosystem & Marketplace",
    desc: "Tracks Marketplace extension installation status, creator asset referral sources, and personalised developer tool recommendations.",
    cookies: [
      ["__kd_mkt", "marketplace/ecosystem", "asset attribution and referral tracking", "60 days"],
    ],
  },
];

// Equal-weight consent button: solid high-contrast, NEVER the #9adee0 accent (fix note 4).
const solidBtn = {
  background: "#FAFAFA", color: "#111113", border: "1px solid #FAFAFA", borderRadius: 999,
  padding: "10px 20px", fontWeight: 600, fontSize: 14, cursor: "pointer",
  fontFamily: "inherit", transition: "opacity .15s cubic-bezier(.4,0,.2,1)",
};
const ghostBtn = {
  background: "transparent", color: "var(--muted)", border: "none", borderRadius: 999,
  padding: "10px 20px", fontWeight: 500, fontSize: 14, cursor: "pointer", fontFamily: "inherit",
};

function detectJurisdiction() {
  try {
    const loc = navigator.language || "en-GB";
    const region = new Intl.Locale(loc).region || loc.split("-")[1];
    if (region) {
      const names = new Intl.DisplayNames(["en"], { type: "region" });
      return names.of(region) || region;
    }
  } catch { /* ignore */ }
  return "UK/EU (default)";
}

function persist(consent) {
  const payload = {
    necessary: true,
    functional: !!consent.functional,
    analytics: !!consent.analytics,
    marketplace: !!consent.marketplace,
    timestamp: new Date().toISOString(),
    version: VERSION,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  api.post("/consents", {
    accept_functional: payload.functional,
    accept_analytics: payload.analytics,
    accept_marketing: payload.marketplace,
    version: VERSION,
  }).catch(() => {});
  return payload;
}

function readConsent() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
}

export default function CookieConsent() {
  const { org } = useAuth();
  const stored = readConsent();
  const [showBanner, setShowBanner] = useState(!stored);
  const [showModal, setShowModal] = useState(false);
  const [sel, setSel] = useState({
    functional: stored?.functional || false,
    analytics: stored?.analytics || false,
    marketplace: stored?.marketplace || false,
  });
  const [open, setOpen] = useState({});
  const modalRef = useRef(null);
  const jurisdiction = detectJurisdiction();
  const workspaceId = org?.id || "Guest session (not signed in)";

  const openPrefs = useCallback(() => {
    const s = readConsent();
    setSel({ functional: s?.functional || false, analytics: s?.analytics || false, marketplace: s?.marketplace || false });
    setShowModal(true);
  }, []);

  // Footer "Cookie Settings" reopen link dispatches this event, so it works site-wide.
  useEffect(() => {
    window.addEventListener("open-cookie-preferences", openPrefs);
    return () => window.removeEventListener("open-cookie-preferences", openPrefs);
  }, [openPrefs]);

  // Esc to close + focus trap while the modal is open.
  useEffect(() => {
    if (!showModal) return;
    const node = modalRef.current;
    const focusables = () => Array.from(node?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || []).filter(el => !el.disabled);
    const f = focusables(); f[0]?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { setShowModal(false); return; }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showModal]);

  const acceptAll = () => { persist({ functional: true, analytics: true, marketplace: true }); setShowBanner(false); setShowModal(false); };
  const rejectNonEssential = () => { persist({ functional: false, analytics: false, marketplace: false }); setShowBanner(false); setShowModal(false); };
  const saveSelection = () => { persist(sel); setShowBanner(false); setShowModal(false); };

  if (!showBanner && !showModal) return null;

  return (
    <>
      {/* Stage 1: floating banner */}
      {showBanner && !showModal && (
        <div data-testid="cookie-banner" role="region" aria-label="Cookie preferences"
          style={{ position: "fixed", bottom: 18, left: 18, right: 18, maxWidth: 860, margin: "0 auto",
            background: "var(--container)", border: "1px solid var(--border)", borderRadius: 14, padding: 22, zIndex: 900 }}>
          <div className="k-heading" style={{ fontWeight: 700, fontSize: 16 }}>Cookie preferences &amp; data protection notice</div>
          <p className="k-muted" style={{ fontSize: 13.5, lineHeight: 1.6, marginTop: 8, maxWidth: 720 }}>
            Kadana uses essential cookies to manage workspace authentication, secure multi-tenant sessions, and maintain
            platform integrity. We also request your consent for optional functional, analytics, and Marketplace cookies —
            see our <a className="k-patent-asterisk" href="/cookies">Cookie Policy</a> for full detail.
          </p>
          <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap", alignItems: "center" }}>
            <button data-testid="cookie-accept-all" style={solidBtn} onClick={acceptAll}>Accept All</button>
            <button data-testid="cookie-reject" style={solidBtn} onClick={rejectNonEssential}>Reject Non-Essential</button>
            <button data-testid="cookie-customize" style={ghostBtn}
              onMouseEnter={e => e.currentTarget.style.background = "var(--control)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              onClick={() => setShowModal(true)}>Customise preferences</button>
          </div>
        </div>
      )}

      {/* Stage 2: preferences modal */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(17,17,19,.7)",
          backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setShowModal(false)}>
          <div ref={modalRef} data-testid="cookie-modal" role="dialog" aria-modal="true"
            aria-label="Manage your cookie preferences" onClick={e => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 640, maxHeight: "88vh", overflowY: "auto", background: "var(--container)",
              border: "1px solid var(--border-strong)", borderRadius: 14, padding: 24, zIndex: 1001 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div>
                <div className="k-heading" style={{ fontWeight: 700, fontSize: 20 }}>Manage your cookie preferences</div>
                <div className="k-muted mono" data-testid="cookie-modal-subtitle" style={{ fontSize: 12, marginTop: 6 }}>
                  Workspace: {workspaceId} | Jurisdiction: {jurisdiction}
                </div>
              </div>
              <button data-testid="cookie-modal-close" aria-label="Close" onClick={() => setShowModal(false)}
                style={{ background: "transparent", border: "none", color: "var(--muted)", cursor: "pointer", padding: 4 }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}>
              {CATEGORIES.map((cat) => {
                const on = cat.locked ? true : sel[cat.key];
                const expanded = !!open[cat.key];
                return (
                  <div key={cat.key} data-testid={`cookie-cat-${cat.key}`}
                    style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 14, background: "var(--canvas)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                      <div className="k-heading" style={{ fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                        {cat.locked && <Lock size={13} />} {cat.title}
                      </div>
                      {/* Toggle */}
                      <button role="switch" aria-checked={on} aria-label={cat.title} disabled={cat.locked}
                        data-testid={`cookie-toggle-${cat.key}`}
                        onClick={() => !cat.locked && setSel(s => ({ ...s, [cat.key]: !s[cat.key] }))}
                        style={{ width: 42, height: 24, borderRadius: 999, border: "1px solid var(--border-strong)",
                          background: on ? "#FAFAFA" : "var(--control)", position: "relative", cursor: cat.locked ? "not-allowed" : "pointer",
                          opacity: cat.locked ? 0.7 : 1, transition: "background .2s ease-in-out", flexShrink: 0 }}>
                        <span style={{ position: "absolute", top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: 999,
                          background: on ? "#111113" : "#A1A1AA", transition: "left .2s ease-in-out" }} />
                      </button>
                    </div>
                    <p className="k-muted" style={{ fontSize: 12.5, lineHeight: 1.55, margin: "8px 0 0" }}>{cat.desc}</p>
                    <button data-testid={`cookie-drawer-${cat.key}`} aria-expanded={expanded}
                      onClick={() => setOpen(o => ({ ...o, [cat.key]: !o[cat.key] }))}
                      style={{ background: "transparent", border: "none", color: "var(--muted)", cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 4, fontSize: 12, padding: "6px 0 0", fontFamily: "inherit" }}>
                      {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />} View {cat.cookies.length} cookie{cat.cookies.length > 1 ? "s" : ""}
                    </button>
                    {expanded && (
                      <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                        {cat.cookies.map(([id, kind, purpose, retention]) => (
                          <div key={id} className="mono" style={{ fontSize: 11, color: "var(--muted)", display: "flex", flexWrap: "wrap", gap: 8,
                            borderTop: "1px solid var(--border)", paddingTop: 6 }}>
                            <span className="k-heading" style={{ fontWeight: 600 }}>{id}</span>
                            <span>· {kind}</span><span>· {purpose}</span><span>· {retention}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer: equal-weight actions (fix note 2) */}
            <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
              <button data-testid="cookie-save" style={solidBtn} onClick={saveSelection}>Save Current Selection</button>
              <button data-testid="cookie-accept-all-modal" style={solidBtn} onClick={acceptAll}>Accept All Cookies</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
